import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  Zap, Plus, Trash2, Play, Circle, ChevronDown, ChevronUp, Clock, CheckCircle2, XCircle, AlertCircle, X, AlertTriangle
} from 'lucide-react'; // Clock already imported, used by Run reminders now button below
import api, { organizationsAPI } from '../services/api';
import TemplateVariablesPanel, { TEMPLATE_FIELD_ATTR } from '../components/TemplateVariablesPanel';

const TRIGGER_LABELS = {
  'lead.created':         { label: 'New lead is created',                   icon: '🆕' },
  'lead.stage_changed':   { label: 'Lead moves to a new stage',             icon: '🔄' },
  'lead.assigned':        { label: 'Lead is assigned to someone',           icon: '👤' },
  'lead.tag_added':       { label: 'Tag added to a lead',                   icon: '🏷️' },
  'note.added':           { label: 'Note is added to a lead',               icon: '📝' },
  'inbound.received':     { label: 'Inbound message received (any channel)', icon: '📥' },
  'task.completed':       { label: 'Task is completed',                     icon: '✅' },
  'task.due_soon':        { label: 'Task is approaching its due date',      icon: '⏰' },
  'bulk_email.opened':    { label: 'Bulk email opened',                     icon: '📨' },
  'bulk_email.clicked':   { label: 'Bulk email link clicked',               icon: '🔗' }
};

const ACTION_LABELS = {
  send_email:      { label: 'Send email',                  icon: '✉️' },
  send_sms:        { label: 'Send SMS',                    icon: '💬' },
  create_task:     { label: 'Create task',                 icon: '✅' },
  assign_to_user:  { label: 'Assign to user',              icon: '👤' },
  move_to_stage:   { label: 'Move to stage',               icon: '➡️' },
  add_tag:         { label: 'Add tag',                     icon: '🏷️' },
  remove_tag:      { label: 'Remove tag',                  icon: '🏷️' },
  set_priority:    { label: 'Set priority',                icon: '⭐' },
  update_field:    { label: 'Update a field',              icon: '✏️' },
  notify_user:     { label: 'Notify a user (in-app)',      icon: '🔔' },
  send_webhook:    { label: 'POST to a webhook URL',       icon: '🌐' },
  archive_lead:    { label: 'Archive lead',                icon: '🗄️' }
};

const RECIPIENT_LABELS = {
  lead:           'Lead (default — sends to lead\'s phone/email)',
  assigned_user:  'Assigned user',
  specific_user:  'A specific user',
  custom:         'Custom phone / email'
};

const CHANNEL_OPTIONS = [
  { value: '', label: 'Any channel' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'meta_ad', label: 'Meta Lead Ad' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'webform', label: 'Web form' },
  { value: 'phone', label: 'Phone call note' },
  { value: 'appointment', label: 'Appointment' }
];

const EMPTY_RULE = {
  name: '',
  description: '',
  trigger: { type: 'lead.created', config: {} },
  filters: [],
  actions: [{ type: 'add_tag', config: { tag: '' } }],
  isActive: true
};

// Spread onto inputs that accept {{variables}} so the Variables panel can
// target them (click-to-insert) and highlight them while dragging.
const TEMPLATE_PROPS = { [TEMPLATE_FIELD_ATTR]: '' };

const Automations = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // null | EMPTY_RULE | existing rule
  const [expandedId, setExpandedId] = useState(null);

  const { data: rules = [], isLoading } = useQuery(
    ['automations'],
    () => api.get('/automations').then((r) => r.data)
  );

  const { data: smtp } = useQuery(
    ['smtp-settings'],
    () => api.get('/smtp/settings').then((r) => r.data),
    { staleTime: 60 * 1000, retry: false }
  );
  const { data: sms } = useQuery(
    ['voip-status'],
    () => api.get('/voip/status').then((r) => r.data),
    { staleTime: 60 * 1000, retry: false }
  );
  const isSmtpConfigured = !!smtp?.isConfigured;
  const isSmsConfigured = sms?.provider === 'voipms' && !!sms?.isConfigured;

  // Share the same cache key as PipelineSettings (`['stages', pipelineId]`) so
  // that adding / renaming / deleting a stage there invalidates this list too.
  // refetchOnMount keeps it fresh whenever the user opens Automations.
  const { data: pipelines = [] } = useQuery(
    ['pipelines'],
    () => api.get('/pipelines').then((r) => r.data)
  );
  const pipelineId = pipelines?.[0]?._id;
  const { data: stages = [] } = useQuery(
    ['stages', pipelineId],
    () => api.get(`/pipelines/${pipelineId}/stages`).then((r) => r.data),
    { enabled: !!pipelineId, refetchOnMount: 'always' }
  );

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];

  const { data: customFields = [] } = useQuery(
    ['custom-fields-for-automations'],
    () => api.get('/custom-fields?entity=lead').then((r) => r.data || []),
    { staleTime: 60_000 }
  );

  const createMutation = useMutation(
    (body) => api.post('/automations', body).then((r) => r.data),
    {
      onSuccess: () => { toast.success('Automation created'); setEditing(null); queryClient.invalidateQueries(['automations']); },
      onError: (err) => toast.error(err.response?.data?.error || 'Create failed')
    }
  );
  const patchMutation = useMutation(
    ({ id, patch }) => api.patch(`/automations/${id}`, patch).then((r) => r.data),
    {
      onSuccess: () => { toast.success('Saved'); setEditing(null); queryClient.invalidateQueries(['automations']); },
      onError: (err) => toast.error(err.response?.data?.error || 'Update failed')
    }
  );
  const toggleMutation = useMutation(
    ({ id, isActive }) => api.patch(`/automations/${id}`, { isActive }).then((r) => r.data),
    { onSuccess: () => queryClient.invalidateQueries(['automations']) }
  );
  const deleteMutation = useMutation(
    (id) => api.delete(`/automations/${id}`).then((r) => r.data),
    { onSuccess: () => { toast.success('Deleted'); queryClient.invalidateQueries(['automations']); } }
  );

  const saveRule = (payload) => {
    if (editing?._id) patchMutation.mutate({ id: editing._id, patch: payload });
    else createMutation.mutate(payload);
  };

  const runDueReminders = useMutation(
    () => api.post('/automations/run-due-reminders').then((r) => r.data),
    {
      onSuccess: (r) => {
        toast.success(`Reminders cron tick ran (${r.durationMs}ms). Check the rule's Recent Runs tab.`);
        queryClient.invalidateQueries(['automations']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Run failed'),
    }
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Zap size={28} /> Automations
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Run actions automatically when events happen. Personalize messages with variables like <code className="text-xs bg-gray-100 px-1 rounded">{'{{firstName}}'}</code> or a <code className="text-xs bg-gray-100 px-1 rounded">{'{{leadUrl}}'}</code> link from the Variables panel.
          </p>
        </div>
        {editing === null && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => runDueReminders.mutate()}
              disabled={runDueReminders.isLoading}
              className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50"
              title="Manually run the task.due_soon cron tick now (don't wait for the hourly schedule)"
            >
              <Clock size={14} /> {runDueReminders.isLoading ? 'Running…' : 'Run reminders now'}
            </button>
            <button
              onClick={() => setEditing({ ...EMPTY_RULE })}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
            >
              <Plus size={14} /> New Automation
            </button>
          </div>
        )}
      </div>

      {editing !== null && (
        <RuleEditor
          value={editing}
          stages={stages}
          orgUsers={orgUsers}
          customFields={customFields}
          isSmtpConfigured={isSmtpConfigured}
          isSmsConfigured={isSmsConfigured}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={saveRule}
          saving={createMutation.isLoading || patchMutation.isLoading}
        />
      )}

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : rules.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
          <Zap size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No automations yet. Create one to run actions on every event.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <RuleCard
              key={rule._id}
              rule={rule}
              expanded={expandedId === rule._id}
              onToggleExpand={() => setExpandedId(expandedId === rule._id ? null : rule._id)}
              onEdit={() => setEditing(rule)}
              onDelete={() => window.confirm(`Delete "${rule.name}"?`) && deleteMutation.mutate(rule._id)}
              onToggleActive={() => toggleMutation.mutate({ id: rule._id, isActive: !rule.isActive })}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RuleCard = ({ rule, expanded, onToggleExpand, onEdit, onDelete, onToggleActive }) => {
  const { data: runs = [] } = useQuery(
    ['automation-runs', rule._id],
    () => api.get(`/automations/${rule._id}/runs`).then((r) => r.data),
    { enabled: expanded, refetchInterval: expanded ? 8000 : false }
  );

  const tMeta = TRIGGER_LABELS[rule.trigger?.type] || { label: rule.trigger?.type, icon: '⚙️' };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 p-4">
        <Circle size={10} className={rule.isActive ? 'text-green-500 fill-green-500' : 'text-gray-300 fill-gray-300'} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{rule.name}</h3>
            {!rule.isActive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Paused</span>}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {tMeta.icon} When <strong>{tMeta.label}</strong> → {rule.actions?.length || 0} action{rule.actions?.length === 1 ? '' : 's'}
            {rule.runCount > 0 && <> · <span className="text-gray-700 font-medium">{rule.runCount}</span> runs{rule.lastRunAt ? `, last ${new Date(rule.lastRunAt).toLocaleDateString()}` : ''}</>}
          </p>
          <div className="flex flex-wrap gap-1 mt-2">
            {(rule.actions || []).map((a, i) => {
              const meta = ACTION_LABELS[a.type] || { label: a.type };
              return <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{meta.icon} {meta.label}</span>;
            })}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onToggleActive} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900">
            {rule.isActive ? 'Pause' : 'Resume'}
          </button>
          <button onClick={onEdit} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900">Edit</button>
          <button onClick={onDelete} className="p-2 text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
          <button onClick={onToggleExpand} className="p-2 text-gray-500">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Recent Runs</h4>
          {runs.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No runs yet.</p>
          ) : (
            <div className="bg-white border border-gray-200 rounded overflow-x-auto">
              <table className="w-full min-w-[720px] text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left p-2 whitespace-nowrap">When</th>
                    <th className="text-left p-2 whitespace-nowrap">Lead</th>
                    <th className="text-left p-2 whitespace-nowrap">Status</th>
                    <th className="text-left p-2">Actions</th>
                    <th className="text-left p-2 whitespace-nowrap">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.slice(0, 20).map((r) => (
                    <tr key={r._id} className="border-t border-gray-100 align-top">
                      <td className="p-2 text-gray-600 whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
                      <td className="p-2 text-gray-700 whitespace-nowrap">{r.leadName || '—'}</td>
                      <td className="p-2 whitespace-nowrap">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="p-2 min-w-[280px]">
                        <div className="space-y-0.5">
                          {(r.actionsRun || []).map((a, i) => (
                            <div key={i} className="flex items-start gap-1 text-[11px]">
                              <ActionIcon status={a.status} />
                              <code className="text-[10px] whitespace-nowrap">{a.type}</code>
                              <span className="text-gray-500 break-words">{a.detail}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="p-2 text-gray-500 whitespace-nowrap">{r.durationMs}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    success: { cls: 'bg-green-100 text-green-700', label: 'success' },
    partial: { cls: 'bg-amber-100 text-amber-700', label: 'partial' },
    failed:  { cls: 'bg-red-100 text-red-700',     label: 'failed' },
    skipped: { cls: 'bg-gray-100 text-gray-600',   label: 'skipped' }
  };
  const m = map[status] || map.skipped;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${m.cls}`}>{m.label}</span>;
};

const ActionIcon = ({ status }) => {
  if (status === 'success') return <CheckCircle2 size={11} className="text-green-600 flex-shrink-0" />;
  if (status === 'failed') return <XCircle size={11} className="text-red-600 flex-shrink-0" />;
  return <AlertCircle size={11} className="text-gray-400 flex-shrink-0" />;
};

const RuleEditor = ({ value, stages, orgUsers = [], customFields = [], isSmtpConfigured, isSmsConfigured, onChange, onCancel, onSave, saving }) => {
  const [localFilters, setLocalFilters] = useState(value.filters || []);

  const set = (patch) => onChange({ ...value, ...patch });
  const setTrigger = (patch) => set({ trigger: { ...value.trigger, ...patch } });
  const setAction = (idx, patch) => {
    const a = [...value.actions];
    a[idx] = { ...a[idx], ...patch };
    set({ actions: a });
  };
  const addAction = () => set({ actions: [...value.actions, { type: 'add_tag', config: { tag: '' } }] });
  const removeAction = (idx) => set({ actions: value.actions.filter((_, i) => i !== idx) });

  // Last message field the user focused: where a clicked variable goes.
  const lastFieldRef = useRef(null);
  const [draggingVar, setDraggingVar] = useState(false);
  const trackField = (e) => {
    if (e.target.hasAttribute?.(TEMPLATE_FIELD_ATTR)) lastFieldRef.current = e.target;
  };
  const showTaskVariables = value.trigger.type === 'task.due_soon' || value.trigger.type === 'task.completed';
  const panelProps = {
    customFields,
    showTaskVariables,
    targetRef: lastFieldRef,
    onDragState: setDraggingVar,
  };

  return (
    <div
      className="mb-6 lg:grid lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-4 lg:items-start"
      data-var-dragging={draggingVar ? 'true' : undefined}
      onFocus={trackField}
    >
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{value._id ? 'Edit automation' : 'New automation'}</h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          value={value.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="e.g. Email new Meta Ads leads"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">1. When this happens</h3>
        <select
          value={value.trigger.type}
          onChange={(e) => setTrigger({ type: e.target.value, config: {} })}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
        >
          {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>

        {value.trigger.type === 'lead.stage_changed' && (
          <div className="mt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <select
                value={value.trigger.config?.fromStageId || ''}
                onChange={(e) => setTrigger({ config: { ...(value.trigger.config || {}), fromStageId: e.target.value || undefined } })}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
              >
                <option value="">Any from-stage</option>
                {stages.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
              <select
                value={value.trigger.config?.toStageId || ''}
                onChange={(e) => setTrigger({ config: { ...(value.trigger.config || {}), toStageId: e.target.value || undefined, toClosedType: undefined } })}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
              >
                <option value="">Any to-stage</option>
                {stages.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                ...or by stage type (covers all custom Won / Lost / DNC stages)
              </label>
              <select
                value={value.trigger.config?.toClosedType || ''}
                onChange={(e) => setTrigger({ config: { ...(value.trigger.config || {}), toClosedType: e.target.value || undefined, toStageId: e.target.value ? undefined : value.trigger.config?.toStageId } })}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md"
                disabled={!!value.trigger.config?.toStageId}
              >
                <option value="">Any closed type</option>
                <option value="won">Won (any won stage)</option>
                <option value="lost">Lost (any lost stage)</option>
                <option value="do_not_call">Do Not Call (any DNC stage)</option>
              </select>
              {value.trigger.config?.toStageId && (
                <p className="mt-1 text-[10px] text-gray-400">Disabled — clear "to-stage" above to use closed type instead.</p>
              )}
            </div>
          </div>
        )}

        {value.trigger.type === 'note.added' && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <select
              value={value.trigger.config?.source || ''}
              onChange={(e) => setTrigger({ config: { ...(value.trigger.config || {}), source: e.target.value || undefined } })}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
            >
              <option value="">Any channel</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="phone">Phone</option>
              <option value="webform">Webform</option>
              <option value="meta_ad">Meta Ad</option>
              <option value="appointment">Appointment</option>
            </select>
            <select
              value={value.trigger.config?.direction || ''}
              onChange={(e) => setTrigger({ config: { ...(value.trigger.config || {}), direction: e.target.value || undefined } })}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
            >
              <option value="">Any direction</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>
        )}

        {value.trigger.type === 'inbound.received' && (
          <div className="mt-2">
            <select
              value={value.trigger.config?.channel || ''}
              onChange={(e) => setTrigger({ config: { ...(value.trigger.config || {}), channel: e.target.value || undefined } })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md"
            >
              {CHANNEL_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">Pick a channel or leave on "Any" to fire for every inbound message.</p>
          </div>
        )}

        {value.trigger.type === 'lead.tag_added' && (
          <input
            value={value.trigger.config?.tag || ''}
            onChange={(e) => setTrigger({ config: { ...(value.trigger.config || {}), tag: e.target.value || undefined } })}
            placeholder="Specific tag (leave empty for any)"
            className="mt-2 w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md font-mono"
          />
        )}

        {value.trigger.type === 'lead.assigned' && (
          <p className="mt-2 text-[11px] text-gray-500">Fires whenever a lead's owner changes — including auto-assignments from inbound messages.</p>
        )}

        {(value.trigger.type === 'bulk_email.opened' || value.trigger.type === 'bulk_email.clicked') && (
          <p className="mt-2 text-[11px] text-gray-500">Fires the first time a recipient opens / clicks. Subsequent opens / clicks are not re-triggered.</p>
        )}

        {value.trigger.type === 'task.completed' && (
          <p className="mt-2 text-[11px] text-gray-500">Fires when a task linked to a lead is marked completed. Filter by task type below if needed.</p>
        )}

        {value.trigger.type === 'task.due_soon' && (
          <TaskDueSoonConfig value={value} setTrigger={setTrigger} />
        )}
      </div>

      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">2. Then do these actions</h3>
        <div className="lg:hidden mb-3">
          <TemplateVariablesPanel {...panelProps} collapsible />
        </div>
        <div className="space-y-3">
          {value.actions.map((a, idx) => (
            <ActionEditor
              key={idx}
              value={a}
              stages={stages}
              orgUsers={orgUsers}
              customFields={customFields}
              isSmtpConfigured={isSmtpConfigured}
              isSmsConfigured={isSmsConfigured}
              onChange={(patch) => setAction(idx, patch)}
              onRemove={value.actions.length > 1 ? () => removeAction(idx) : null}
            />
          ))}
        </div>
        <button type="button" onClick={addAction} className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700">
          <Plus size={12} /> Add another action
        </button>
      </div>

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={value.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
        Active (runs on every matching event)
      </label>

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
        <button
          onClick={() => onSave(value)}
          disabled={saving}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
        >
          {saving ? 'Saving...' : 'Save automation'}
        </button>
      </div>
    </div>
      <aside className="hidden lg:block lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
        <TemplateVariablesPanel {...panelProps} />
      </aside>
    </div>
  );
};

// Trigger config UI for task.due_soon. Stores its own unit-of-measure so
// switching the dropdown between minutes/hours/days survives the value=0
// case (where 0 is the same in every unit and an "auto-derive from total
// minutes" approach would always snap back to "minutes"). Writes to
// trigger.config.offsetMinutes whenever the value or unit changes.
const TaskDueSoonConfig = ({ value, setTrigger }) => {
  const cfg = value.trigger.config || {};
  const totalMin = Number.isFinite(Number(cfg.offsetMinutes))
    ? Number(cfg.offsetMinutes)
    : ((Number(cfg.daysBefore) || 0) * 1440 + (Number(cfg.hoursBefore) || 0) * 60 + (Number(cfg.minutesBefore) || 0));

  // One-shot init: pick the most natural unit/value pair from totalMin so
  // existing rules render the way the operator originally wrote them.
  // Subsequent changes go through React state, not derivation.
  const initial = React.useMemo(() => {
    if (totalMin > 0 && totalMin % 1440 === 0) return { value: totalMin / 1440, unit: 'days' };
    if (totalMin > 0 && totalMin % 60 === 0) return { value: totalMin / 60, unit: 'hours' };
    return { value: totalMin, unit: 'minutes' };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [val, setVal] = React.useState(String(initial.value));
  const [unit, setUnit] = React.useState(initial.unit);

  const writeOffset = (nextVal, nextUnit) => {
    const v = Math.max(0, Number(nextVal) || 0);
    const minutes = nextUnit === 'days' ? v * 1440 : nextUnit === 'hours' ? v * 60 : v;
    setTrigger({
      config: {
        ...cfg,
        offsetMinutes: minutes,
        daysBefore: undefined,
        hoursBefore: undefined,
        minutesBefore: undefined,
      },
    });
  };

  return (
    <div className="mt-2 space-y-2">
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
        Fire reminder
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={9999}
          value={val}
          onChange={(e) => {
            setVal(e.target.value);
            writeOffset(e.target.value, unit);
          }}
          className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-md"
        />
        <select
          value={unit}
          onChange={(e) => {
            setUnit(e.target.value);
            writeOffset(val, e.target.value);
          }}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md"
        >
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
        <span className="text-sm text-gray-500">before due date</span>
      </div>

      <div>
        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1 mt-2">
          Task type (optional)
        </label>
        <select
          value={cfg.taskType || ''}
          onChange={(e) => setTrigger({ config: { ...cfg, taskType: e.target.value || undefined } })}
          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md"
        >
          <option value="">Any task type</option>
          <option value="todo">To-do</option>
          <option value="call">Call</option>
          <option value="meeting">Meeting</option>
          <option value="deadline">Deadline</option>
          <option value="email">Email</option>
        </select>
      </div>
      <div className="text-[11px] text-gray-500 leading-snug space-y-1.5">
        <p>
          Cron checks hourly by default; fires once per (task, rule) pair when the task's due date is the configured offset away (±half the cron interval). Failed runs retry on the next tick. Use the <strong>Run reminders now</strong> button to test without waiting.
        </p>
        <p>Task variables like <code>{'{{taskTitle}}'}</code> and <code>{'{{taskDueDate}}'}</code> are in the Variables panel.</p>
      </div>
    </div>
  );
};

const RecipientPicker = ({ cfg, setCfg, want, orgUsers }) => {
  const recipient = cfg.recipient || { type: 'lead' };
  const setRecipient = (patch) => setCfg({ recipient: { ...recipient, ...patch } });
  return (
    <div className="grid grid-cols-2 gap-2 items-start">
      <select
        value={recipient.type}
        onChange={(e) => setRecipient({ type: e.target.value })}
        className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
      >
        {Object.entries(RECIPIENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {recipient.type === 'specific_user' && (
        <select
          value={recipient.userId || ''}
          onChange={(e) => setRecipient({ userId: e.target.value })}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
        >
          <option value="">Select user...</option>
          {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
        </select>
      )}
      {recipient.type === 'custom' && (
        <input
          value={want === 'phone' ? (recipient.phone || '') : (recipient.email || '')}
          onChange={(e) => setRecipient(want === 'phone' ? { phone: e.target.value } : { email: e.target.value })}
          placeholder={want === 'phone' ? 'Phone (e.g. +15551234567)' : 'Email address'}
          className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
        />
      )}
    </div>
  );
};

const ActionEditor = ({ value, stages, orgUsers = [], customFields = [], isSmtpConfigured, isSmsConfigured, onChange, onRemove }) => {
  const cfg = value.config || {};
  const setCfg = (patch) => onChange({ ...value, config: { ...cfg, ...patch } });
  // Re-render tick after Notification.requestPermission() resolves, so the
  // "blocked in your browser" warning appears/disappears without a reload.
  const [, setPermTick] = useState(0);

  const warning = value.type === 'send_email' && !isSmtpConfigured
    ? { text: 'SMTP is not configured — this action will fail every time it runs.', href: '/smtp-settings', label: 'Configure SMTP' }
    : value.type === 'send_sms' && !isSmsConfigured
      ? { text: 'Gain Rhino VoIP SMS is not configured — this action will fail every time it runs.', href: '/voip-settings', label: 'Open VoIP Settings' }
      : null;

  return (
    <div className="p-3 bg-gray-50 rounded-lg space-y-2">
      <div className="flex items-center gap-2">
        <select
          value={value.type}
          onChange={(e) => onChange({ type: e.target.value, config: {} })}
          className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
        >
          {Object.entries(ACTION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
        {onRemove && (
          <button type="button" onClick={onRemove} className="text-gray-400 hover:text-red-600">
            <X size={14} />
          </button>
        )}
      </div>

      {warning && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
          <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-amber-900">
            {warning.text}{' '}
            <Link to={warning.href} className="underline font-medium">{warning.label} →</Link>
          </div>
        </div>
      )}

      {value.type === 'send_email' && (
        <>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Recipient</label>
          <RecipientPicker cfg={cfg} setCfg={setCfg} want="email" orgUsers={orgUsers} />
          <input value={cfg.subject || ''} onChange={(e) => setCfg({ subject: e.target.value })}
            placeholder="Subject (e.g. Hi {{firstName}})" {...TEMPLATE_PROPS} className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
          <textarea value={cfg.message || ''} onChange={(e) => setCfg({ message: e.target.value })}
            placeholder="Body (add variables like {{firstName}} from the panel)" rows={4} {...TEMPLATE_PROPS}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
        </>
      )}

      {value.type === 'send_sms' && (
        <>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Recipient</label>
          <RecipientPicker cfg={cfg} setCfg={setCfg} want="phone" orgUsers={orgUsers} />
          <textarea value={cfg.message || ''} onChange={(e) => setCfg({ message: e.target.value })}
            placeholder="Message (160 chars recommended)" rows={2} {...TEMPLATE_PROPS}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
        </>
      )}

      {value.type === 'create_task' && (
        <>
          <input value={cfg.title || ''} onChange={(e) => setCfg({ title: e.target.value })}
            placeholder="Task title (e.g. Follow up with {{firstName}})" {...TEMPLATE_PROPS} className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
          <div className="grid grid-cols-2 gap-2">
            <select value={cfg.type || 'todo'} onChange={(e) => setCfg({ type: e.target.value })}
              className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white">
              <option value="todo">To-do</option>
              <option value="call">Call</option>
              <option value="meeting">Meeting</option>
              <option value="deadline">Deadline</option>
              <option value="email">Email</option>
            </select>
            <input type="number" value={cfg.dueInDays || ''} onChange={(e) => setCfg({ dueInDays: Number(e.target.value) })}
              placeholder="Due in (days)" className="px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
          </div>
        </>
      )}

      {value.type === 'move_to_stage' && (
        <select value={cfg.stageId || ''} onChange={(e) => setCfg({ stageId: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white">
          <option value="">Select stage...</option>
          {stages.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      )}

      {value.type === 'add_tag' && (
        <input value={cfg.tag || ''} onChange={(e) => setCfg({ tag: e.target.value })}
          placeholder="Tag (e.g. hot-lead)" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
      )}

      {value.type === 'remove_tag' && (
        <input value={cfg.tag || ''} onChange={(e) => setCfg({ tag: e.target.value })}
          placeholder="Tag to remove" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
      )}

      {value.type === 'assign_to_user' && (
        <select value={cfg.userId || ''} onChange={(e) => setCfg({ userId: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white">
          <option value="">Select user...</option>
          {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
        </select>
      )}

      {value.type === 'set_priority' && (
        <select value={cfg.priority || ''} onChange={(e) => setCfg({ priority: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white">
          <option value="">Select priority...</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      )}

      {value.type === 'update_field' && (
        <>
          <select value={cfg.field || ''} onChange={(e) => setCfg({ field: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white">
            <option value="">Select field...</option>
            <optgroup label="Built-in fields">
              <option value="status">Status</option>
              <option value="priority">Priority</option>
              <option value="source">Source</option>
              <option value="estimatedValue">Estimated value</option>
              <option value="company">Company</option>
              <option value="position">Position / title</option>
            </optgroup>
            {customFields.length > 0 && (
              <optgroup label="Custom fields">
                {customFields.map((cf) => (
                  <option key={cf._id || cf.key} value={`customFields.${cf.key}`}>{cf.label}</option>
                ))}
              </optgroup>
            )}
          </select>
          <input value={cfg.value ?? ''} onChange={(e) => setCfg({ value: e.target.value })}
            placeholder="Value (supports {{firstName}} etc.)" {...TEMPLATE_PROPS}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
        </>
      )}

      {value.type === 'notify_user' && (
        <>
          <select value={cfg.userId || ''} onChange={(e) => setCfg({ userId: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white">
            <option value="">Lead's assignee (default)</option>
            <option value="whole_team">🟢 Whole team (everyone in the org)</option>
            {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
          </select>
          <input value={cfg.title || ''} onChange={(e) => setCfg({ title: e.target.value })}
            placeholder="Notification title (e.g. New hot lead: {{firstName}})" {...TEMPLATE_PROPS}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
          <input value={cfg.message || ''} onChange={(e) => setCfg({ message: e.target.value })}
            placeholder="Body (optional)" {...TEMPLATE_PROPS}
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white" />
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.showPopup === true}
              onChange={(e) => {
                const on = e.target.checked;
                setCfg({ showPopup: on });
                // Browsers only allow the permission prompt from a user
                // gesture — this click is our one clean shot at it.
                if (on && typeof Notification !== 'undefined' && Notification.permission === 'default') {
                  Notification.requestPermission().then(() => setPermTick((t) => t + 1));
                }
              }}
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700">
              Also show on-screen popup
              <span className="block text-[11px] text-gray-500">
                Toast inside the app; desktop notification when the tab is in background.
              </span>
            </span>
          </label>
          {cfg.showPopup === true && typeof Notification !== 'undefined' && Notification.permission !== 'granted' && (
            <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
              <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-amber-900">
                {Notification.permission === 'denied' ? (
                  'Desktop notifications are blocked in your browser — only in-app toasts will show. Allow notifications for this site in your browser settings to fix it.'
                ) : (
                  <>
                    Desktop notifications are not enabled in this browser yet — background-tab popups won't show.{' '}
                    <button
                      type="button"
                      onClick={() => Notification.requestPermission().then(() => setPermTick((t) => t + 1))}
                      className="underline font-medium"
                    >
                      Enable desktop notifications →
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {value.type === 'send_webhook' && (
        <>
          <input value={cfg.url || ''} onChange={(e) => setCfg({ url: e.target.value })}
            placeholder="https://hooks.example.com/..."
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white font-mono" />
          <input value={cfg.secret || ''} onChange={(e) => setCfg({ secret: e.target.value })}
            placeholder="Optional shared secret (sent as X-Gainrhino-Secret header)"
            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white font-mono" />
          <p className="text-[11px] text-gray-500 leading-snug">
            POSTs JSON with full lead, custom fields, and stage transition details. Pair with a "Lead is Won" trigger to push converted patients into OpenDental or any external system.
          </p>
        </>
      )}

      {value.type === 'archive_lead' && (
        <p className="text-[11px] text-gray-500 italic">No options — the lead will be archived (hidden from default lists).</p>
      )}
    </div>
  );
};

export default Automations;
